/* ═══════════════════════════════════════════════════════════════
   Forma Studio's panels — ONE set, mounted by every forma editor.

   Josh's ruling (docs/PROJECT_EDITOR_PLAN.md): one kit per tool, identical
   in and out of projects — never a second copy. Forma Studio and the
   project editor (Designer) both register these dock panels and both get
   their paper wiring from this hook: the same Typography / Page Margins /
   Formats / Layout Options / Watermark / Section End / Tokens / Levels
   bodies, the same token chip selection + copy/cut/paste triad (DESIGN.md
   §4 ABSOLUTE), the same drop/insert/replace gestures on the sheet.

   There is NO "Header & Footer" panel. The sheet is the one place zones
   are worked (ZoneBandEdit): precise placement, reordering, line breaks,
   the {Line} rule, per-column clipboard, per-token format/size/spacing.

   The only differences between hosts are data, never UI: a project
   resolves placeholders and project tokens (`project`), a master shows
   them braced; the Designer's Levels panel follows the document caret
   (`editor`), the Studio's has no document.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Editor } from '@tiptap/react';
import {
  Braces,
  Droplets,
  Flag,
  Hash,
  LayoutTemplate,
  ListOrdered,
  Ruler,
  Type,
} from 'lucide-react';
import type { EmbeddedImage, StandardConfig } from '../../shared/types';
import type { PaletteDef } from '../components/FormaDock';
import { Icon } from '../components/ui/Icon';
import { DocSettings } from '../components/EditorComponents';
import {
  CanvasSelectionContext,
  TokenClipboardContext,
  type CanvasSelectValue,
  type TokenClipboardOps,
} from './TokenComponents';
import {
  setPlaceholderMemories,
  setPlaceholderProjectMode,
  setPlaceholderValues,
  setProjectTokenContents,
  setProjectTokenLabels,
} from '../utils';
import { resolvePlaceholders, type PlaceholderContext } from '../../shared/forma-placeholders';
import { FormaService } from '../services/formaService';
import { reportError } from '../services/errorLog';
import type { FormaSelection } from './useFormaSelection';
import {
  type BandKind,
  type PageVariant,
  type StyleSelection,
  type ZoneColumn,
  zoneAppend,
  zoneApplyToKeys,
  zoneDeleteKeys,
  zoneInsertAt,
  zoneKeyFor,
  variantToAdopt,
  zoneMoveTokens,
  zoneMoveTokensAt,
  zoneRemoveAt,
  zoneReplaceAt,
  readZone,
  parseTokenKey,
} from './formaZonesModel';
import {
  LayoutPalette,
  LevelPalette,
  TextTokenDialog,
  TokensPalette,
  type FormaEdit,
  type TextDialogRequest,
} from './panels/FormaPalettes';
import type { DesignWiring, ZoneDropData } from './designWiring';
import { WatermarkPanel } from './panels/WatermarkPanel';
import { EosPanel } from './panels/EosPanel';

/** The forma panels, as dock panel definitions. Every forma editor registers
 *  these (plus its own canvas and any panels only it has). */
export const FormaEditorPanels: PaletteDef[] = [
  {
    id: 'typography',
    title: 'Typography',
    width: 320,
    defaultPosition: { direction: 'right', reference: 'canvas' },
  },
  {
    id: 'margins',
    title: 'Page Margins',
    width: 320,
    defaultPosition: { direction: 'within', reference: 'typography' },
  },
  {
    id: 'formats',
    title: 'Formats',
    width: 320,
    defaultPosition: { direction: 'within', reference: 'typography' },
  },
  {
    id: 'layout',
    title: 'Layout Options',
    width: 340,
    defaultPosition: { direction: 'within', reference: 'typography' },
  },
  {
    id: 'watermark',
    title: 'Watermark',
    width: 320,
    defaultPosition: { direction: 'within', reference: 'typography' },
  },
  {
    id: 'eos',
    title: 'Section End',
    width: 340,
    defaultPosition: { direction: 'within', reference: 'typography' },
  },
  // Tokens carries BOTH field tokens and project tokens as labeled sections
  // in one body (Josh, 2026-08-26 — the separate "Project Tokens" palette
  // shipped v2.14.47 is retired; same chips, same drag/click behaviour, one
  // home).
  {
    id: 'tokens',
    title: 'Tokens',
    width: 340,
    defaultPosition: { direction: 'left', reference: 'canvas' },
  },
  // No "Header & Footer" panel: the SHEET is the surface for header/footer/
  // pre/post work (Josh, 2026-08-13, Order 1) and carries the complete token
  // model, so a second list-shaped copy of the same three columns would be
  // old UI competing with the paper.
  {
    id: 'levels',
    title: 'Levels',
    width: 340,
    defaultClosed: true,
    defaultPosition: { direction: 'within', reference: 'tokens' },
  },
];

/** The same panels with a host's own default placement. Only WHERE a panel
 *  first lands may differ per host — never its body, id or title. */
export function formaEditorPanels(
  placement: Partial<Record<string, Pick<PaletteDef, 'defaultPosition' | 'defaultClosed'>>> = {}
): PaletteDef[] {
  return FormaEditorPanels.map((p) => (placement[p.id] ? { ...p, ...placement[p.id] } : p));
}

/** The glyph on each forma panel's top-bar button. */
export const FORMA_PANEL_ICONS: Record<string, React.ReactNode> = {
  typography: <Icon icon={Type} em={1.1538} aria-hidden="true" />,
  margins: <Icon icon={Ruler} em={1.1538} aria-hidden="true" />,
  formats: <Icon icon={Hash} em={1.1538} aria-hidden="true" />,
  layout: <Icon icon={LayoutTemplate} em={1.1538} aria-hidden="true" />,
  watermark: <Icon icon={Droplets} em={1.1538} aria-hidden="true" />,
  eos: <Icon icon={Flag} em={1.1538} aria-hidden="true" />,
  tokens: <Icon icon={Braces} em={1.1538} aria-hidden="true" />,
  levels: <Icon icon={ListOrdered} em={1.1538} aria-hidden="true" />,
};

const FORMA_PANEL_IDS = new Set(FormaEditorPanels.map((p) => p.id));
/** Is this dock panel id one of the forma panels? */
export const isFormaPanel = (id: string): boolean => FORMA_PANEL_IDS.has(id);

/** One clipboard for token chips — module scope (survives panel re-renders,
 *  dies with the window), shared by every forma editor. */
let tokenClipboard: string[] = [];

/** Project data a project copy resolves (the Designer). Absent → master
 *  authoring: placeholders and project tokens stay braced. */
export interface FormaEditorProject {
  placeholderCtx: PlaceholderContext;
  projectTokens: Array<{ id: string; name: string; content: string }>;
  issuanceNames: string[];
}

export interface FormaEditorPanelsOpts {
  forma: StandardConfig | null;
  edit: FormaEdit;
  canEdit: boolean;
  toast: (msg: string, isError?: boolean) => void;
  /** The ONE selection (useFormaSelection) — owned by the host editor. */
  sel: FormaSelection;
  pageVariant: PageVariant;
  onPageVariant: (v: PageVariant) => void;
  onDragState: (on: boolean) => void;
  dragActive: boolean;
  /** Open a tool panel by id — the paper's end-of-section line uses it. */
  onOpenPanel?: (id: string) => void;
  project?: FormaEditorProject;
  /** The live document, when there is one — Levels follows its caret. */
  editor?: Editor | null;
  /** reportError tag for the custom-token store. */
  logTag: string;
}

export interface FormaEditorPanelsApi {
  /** Hand to the paper (ZoneShell + ZoneBandEdit read it). */
  wiring: DesignWiring;
  /** The body of a forma panel, or null for an id that is not one. */
  renderPanel: (id: string) => React.ReactNode;
  /** Wrap the workspace: the selection + clipboard contexts and the shared
   *  literal-text dialog. */
  wrap: (children: React.ReactNode) => React.ReactNode;
}

export function useFormaEditorPanels({
  forma,
  edit,
  canEdit,
  toast,
  sel,
  pageVariant,
  onPageVariant,
  onDragState,
  dragActive,
  onOpenPanel,
  project,
  editor,
  logTag,
}: FormaEditorPanelsOpts): FormaEditorPanelsApi {
  // ─── Chip selection + the copy/cut/paste triad (DESIGN.md §4 ABSOLUTE) ───
  // The selection itself lives in the host (useFormaSelection) — ONE state
  // shared with the Format Bar and the paper. Here it is only adapted to the
  // CanvasSelectionContext the chips and bands already speak.
  const { selKey, menuKey, menuOwner, select, selectMany, clearZone } = sel;
  const selectedTokens = sel.tokens;
  const styleSel = sel.styleSel;
  const levelFocus = sel.levelFocus;
  const setBand = sel.setBand;
  const selectLevel = sel.selectLevel;
  // Pages view: a band carries its page's variant. Selecting it makes that
  // the edited variant, so the Editing row and every panel agree with what
  // was clicked (one selection system). The switch it causes is not a
  // "user picked another page" switch, so it keeps the selection.
  const adoptedRef = React.useRef<PageVariant | null>(null);
  const adoptVariant = React.useCallback(
    (clicked: PageVariant | undefined) => {
      const next = variantToAdopt(clicked, pageVariant);
      if (!next) return;
      adoptedRef.current = next;
      onPageVariant(next);
    },
    [pageVariant, onPageVariant]
  );
  const onStyleSelect = React.useCallback(
    (next: StyleSelection) => {
      if (next?.kind === 'zone') adoptVariant(next.variant);
      setBand(next?.kind === 'zone' ? next.band : null);
    },
    [setBand, adoptVariant]
  );
  const onLevelFocus = React.useCallback((level: number) => selectLevel(level), [selectLevel]);

  // Switching page variants MUST drop the zone selection — a leftover one
  // would edit the page you are NOT looking at. (A switch made BY selecting
  // a band on that page keeps it.)
  React.useEffect(() => {
    const adopted = adoptedRef.current;
    adoptedRef.current = null;
    if (adopted && adopted === pageVariant) return;
    clearZone();
  }, [pageVariant, clearZone]);

  const applyToSelection = React.useCallback(
    (fn: (value: string) => string) => {
      if (!selectedTokens.length) return;
      edit((f) => zoneApplyToKeys(f, selectedTokens, fn));
    },
    [edit, selectedTokens]
  );

  const moveTokens = React.useCallback(
    (targetZoneKey: string, keys: string[], copy: boolean) => {
      edit((f) => zoneMoveTokens(f, targetZoneKey, keys, copy));
      clearZone();
    },
    [edit, clearZone]
  );

  // A positioned drop: the tokens land AT the slot the architect saw open.
  const moveTokensAt = React.useCallback<CanvasSelectValue['moveTokensAt']>(
    (targetZoneKey, idx, keys, copy, newLine) => {
      edit((f) => zoneMoveTokensAt(f, keys, targetZoneKey, idx, copy, newLine));
      clearZone();
    },
    [edit, clearZone]
  );

  const canvasSelValue = React.useMemo<CanvasSelectValue>(
    () => ({
      selKey,
      menuKey,
      menuOwner,
      selectedTokens,
      select,
      applyToSelection,
      moveTokens,
      moveTokensAt,
      selectMany,
    }),
    [
      selKey,
      menuKey,
      menuOwner,
      selectedTokens,
      select,
      applyToSelection,
      moveTokens,
      moveTokensAt,
      selectMany,
    ]
  );

  const clipOps = React.useMemo<TokenClipboardOps>(() => {
    const valueAt = (k: string) => {
      if (!forma) return undefined;
      const { zoneKey, idx } = parseTokenKey(k);
      return readZone(forma, zoneKey)[idx];
    };
    return {
      copyKeys: (keys) => {
        const values = keys
          .filter((k) => k.startsWith('token:'))
          .map(valueAt)
          .filter((v): v is string => v != null);
        if (values.length) tokenClipboard = values;
      },
      cutKeys: (keys) => {
        const tokenKeys = keys.filter((k) => k.startsWith('token:'));
        const values = tokenKeys.map(valueAt).filter((v): v is string => v != null);
        if (values.length) tokenClipboard = values;
        edit((f) => zoneDeleteKeys(f, tokenKeys));
        clearZone();
      },
      copySlot: (zoneKey) => {
        if (forma) tokenClipboard = [...readZone(forma, zoneKey)];
      },
      cutSlot: (zoneKey) => {
        if (!forma) return;
        const arr = readZone(forma, zoneKey);
        tokenClipboard = [...arr];
        edit((f) =>
          zoneDeleteKeys(
            f,
            arr.map((_v, i) => `token:${zoneKey}:${i}`)
          )
        );
      },
      pasteAt: (targetKey) => {
        if (!tokenClipboard.length) return;
        const values = [...tokenClipboard];
        if (targetKey.startsWith('token:')) {
          const { zoneKey, idx } = parseTokenKey(targetKey);
          edit((f) => {
            let next = f;
            values.forEach((v, i) => {
              next = zoneInsertAt(next, zoneKey, idx + 1 + i, v);
            });
            return next;
          });
        } else if (targetKey.startsWith('slot:')) {
          const zoneKey = targetKey.slice('slot:'.length);
          edit((f) => {
            let next = f;
            for (const v of values) next = zoneAppend(next, zoneKey, v);
            return next;
          });
        }
      },
      canPaste: () => tokenClipboard.length > 0,
    };
  }, [edit, forma, clearZone]);

  // Keyboard: the triad + Delete on the CHIP selection. A document's own
  // keys win whenever the caret is in it — a chip selection is what makes
  // them mean "the chip" instead. Escape is the selection's own listener.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tgt = e.target as HTMLElement | null;
      if (tgt && (/^(input|textarea|select)$/i.test(tgt.tagName) || tgt.isContentEditable)) return;
      const mod = e.ctrlKey || e.metaKey;
      const tokenSel = selectedTokens.filter((k) => k.startsWith('token:'));
      const slotZone = selKey?.startsWith('slot:') ? selKey.slice('slot:'.length) : null;
      if (mod && (e.key === 'c' || e.key === 'C') && (tokenSel.length || slotZone)) {
        e.preventDefault();
        if (tokenSel.length) clipOps.copyKeys(tokenSel);
        else if (slotZone) clipOps.copySlot(slotZone);
      } else if (mod && (e.key === 'x' || e.key === 'X') && (tokenSel.length || slotZone)) {
        e.preventDefault();
        if (tokenSel.length) clipOps.cutKeys(tokenSel);
        else if (slotZone) clipOps.cutSlot(slotZone);
      } else if (mod && (e.key === 'v' || e.key === 'V') && (selKey || slotZone)) {
        if (selKey) {
          e.preventDefault();
          clipOps.pasteAt(selKey);
        }
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && tokenSel.length) {
        e.preventDefault();
        edit((f) => zoneDeleteKeys(f, tokenSel));
        clearZone();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [clipOps, edit, clearZone, selKey, selectedTokens]);

  // ─── Custom tokens (firm-level, one store for every forma editor) ───
  const [savedCustomTokens, setSavedCustomTokens] = React.useState<string[]>([]);
  const [recentCustomTokens, setRecentCustomTokens] = React.useState<string[]>([]);
  React.useEffect(() => {
    let live = true;
    FormaService.loadTokens()
      .then((t) => {
        if (live) setSavedCustomTokens(t);
      })
      .catch((err) => reportError(`${logTag}:loadTokens`, err));
    return () => {
      live = false;
    };
  }, [logTag]);
  const persistSaved = (next: string[]) => {
    setSavedCustomTokens(next);
    FormaService.saveTokens(next).catch((err) => reportError(`${logTag}:saveTokens`, err));
  };
  const noteCustomToken = React.useCallback((t: string) => {
    setRecentCustomTokens((p) => [t, ...p.filter((x) => x !== t)].slice(0, 10));
  }, []);

  // ─── Text dialog + image picker (shared by panels and paper drops) ───
  const [textDialog, setTextDialog] = React.useState<TextDialogRequest | null>(null);
  const requestImage = React.useCallback(async (): Promise<string | null> => {
    if (!window.api?.openImageDialog) {
      toast('Adding images needs the desktop app', true);
      return null;
    }
    const imgData = (await window.api.openImageDialog()) as EmbeddedImage | null;
    if (!imgData) return null;
    const imgId = 'img_' + Date.now().toString(36);
    edit((f) => ({ ...f, images: { ...(f.images || {}), [imgId]: imgData } }));
    return `{Image:${imgId}}`;
  }, [edit, toast]);

  // Published during RENDER (not an effect): the zone chips read these
  // registries while they render. A master defines {P#} placeholders but
  // resolves nothing — a project's copy is what binds them.
  const placeholderCtx = project?.placeholderCtx;
  const projectTokens = project?.projectTokens;
  setPlaceholderProjectMode(!!project && !!forma?.projectId);
  setPlaceholderMemories(
    Object.fromEntries(Object.entries(forma?.placeholders || {}).map(([id, p]) => [id, p.was]))
  );
  setPlaceholderValues(project && forma ? resolvePlaceholders(forma, placeholderCtx ?? {}) : {});
  setProjectTokenLabels(Object.fromEntries((projectTokens ?? []).map((t) => [t.id, t.name])));
  setProjectTokenContents(Object.fromEntries((projectTokens ?? []).map((t) => [t.id, t.content])));

  const onZoneDrop = React.useCallback(
    (band: BandKind, col: ZoneColumn, d: ZoneDropData, variant?: PageVariant) => {
      const zoneKey = zoneKeyFor(band, variant ?? pageVariant, col);
      onStyleSelect({ kind: 'zone', band, variant });
      if (d.type === 'tokenMulti' && d.keys?.length) {
        moveTokens(zoneKey, d.keys, !!d.copy);
        return;
      }
      const value = d.value;
      if (value == null) return;
      if (d.type === 'token' && d.copy && d.sourceZone != null && d.sourceIndex != null) {
        edit((f) => zoneMoveTokens(f, zoneKey, [`token:${d.sourceZone}:${d.sourceIndex}`], true));
        return;
      }
      if (value === '__custom__') {
        const srcZ = d.sourceZone ?? null;
        const srcI = d.sourceIndex ?? null;
        setTextDialog({
          title: 'Custom text',
          initial: '',
          onCommit: (text) => {
            if (!text) return;
            edit((f) => {
              let next = f;
              if (srcZ != null && srcI != null) next = zoneRemoveAt(next, srcZ, srcI);
              return zoneAppend(next, zoneKey, text);
            });
            noteCustomToken(text);
          },
        });
        return;
      }
      if (value === '{Image}' && d.sourceZone == null) {
        void requestImage().then((tok) => {
          if (tok) edit((f) => zoneAppend(f, zoneKey, tok));
        });
        return;
      }
      edit((f) => {
        let next = f;
        if (d.sourceZone != null && d.sourceIndex != null) {
          next = zoneRemoveAt(next, d.sourceZone, d.sourceIndex);
        }
        return zoneAppend(next, zoneKey, value);
      });
    },
    [edit, moveTokens, noteCustomToken, onStyleSelect, pageVariant, requestImage]
  );

  // Click-to-add lands where the architect POINTED. A column selected on the
  // sheet wins; then a selected token (insert right after it); only then the
  // band-level guess.
  const insertFromPalette = React.useCallback(
    (value: string) => {
      let zoneKey: string | null = null;
      let at: number | null = null;
      if (selKey?.startsWith('slot:')) {
        zoneKey = selKey.slice('slot:'.length);
      } else if (selKey?.startsWith('token:')) {
        const parsed = parseTokenKey(selKey);
        zoneKey = parsed.zoneKey;
        at = parsed.idx + 1;
      } else if (styleSel?.kind === 'zone' && forma) {
        const band = styleSel.band;
        const withContent = (['C', 'L', 'R'] as ZoneColumn[]).find(
          (c) => readZone(forma, zoneKeyFor(band, pageVariant, c)).length > 0
        );
        zoneKey = zoneKeyFor(band, pageVariant, withContent ?? 'C');
      }
      if (!zoneKey) {
        toast('Click a band or a column on the paper first, then click a chip to add it');
        return;
      }
      const zk = zoneKey;
      const idx = at;
      const place = (f: StandardConfig, v: string) =>
        idx == null ? zoneAppend(f, zk, v) : zoneInsertAt(f, zk, idx, v);
      if (value === '{Image}') {
        void requestImage().then((tok) => {
          if (tok) edit((f) => place(f, tok));
        });
        return;
      }
      edit((f) => place(f, value));
    },
    [edit, forma, pageVariant, requestImage, selKey, styleSel, toast]
  );

  // ─── Placing a dropped token EXACTLY ───
  // A drop onto a token on the sheet inserts before/after it, starts a new
  // line above/below, or replaces it.
  const onZoneInsertAt = React.useCallback(
    (
      zoneKey: string,
      idx: number,
      value: string,
      srcZone: string | null,
      srcIdx: number | null,
      newLine: boolean
    ) => {
      if (value === '__custom__') {
        setTextDialog({
          title: 'Custom text',
          initial: '',
          onCommit: (text) => {
            if (!text) return;
            edit((f) => zoneInsertAt(f, zoneKey, idx, text, srcZone, srcIdx, newLine));
            noteCustomToken(text);
          },
        });
        return;
      }
      if (value === '{Image}' && srcZone == null) {
        void requestImage().then((tok) => {
          if (tok) edit((f) => zoneInsertAt(f, zoneKey, idx, tok, null, null, newLine));
        });
        return;
      }
      edit((f) => zoneInsertAt(f, zoneKey, idx, value, srcZone, srcIdx, newLine));
    },
    [edit, noteCustomToken, requestImage]
  );

  const onZoneReplaceAt = React.useCallback(
    (
      zoneKey: string,
      idx: number,
      value: string,
      srcZone: string | null,
      srcIdx: number | null
    ) => {
      if (value === '__custom__') {
        setTextDialog({
          title: 'Custom text',
          initial: '',
          onCommit: (text) => {
            if (!text) return;
            edit((f) => zoneReplaceAt(f, zoneKey, idx, text, srcZone, srcIdx));
            noteCustomToken(text);
          },
        });
        return;
      }
      if (value === '{Image}' && srcZone == null) {
        void requestImage().then((tok) => {
          if (tok) edit((f) => zoneReplaceAt(f, zoneKey, idx, tok, null, null));
        });
        return;
      }
      edit((f) => zoneReplaceAt(f, zoneKey, idx, value, srcZone, srcIdx));
    },
    [edit, noteCustomToken, requestImage]
  );

  // The empty-band hint on the paper is the fast path to a zone's first
  // token — the SAME literal-text dialog, into the COLUMN that was clicked.
  const onZoneQuickAdd = React.useCallback(
    (band: BandKind, col: ZoneColumn, variant?: PageVariant) => {
      const zoneKey = zoneKeyFor(band, variant ?? pageVariant, col);
      onStyleSelect({ kind: 'zone', band, variant });
      select(`slot:${zoneKey}`);
      setTextDialog({
        title: 'Add text',
        initial: '',
        onCommit: (text) => {
          if (!text) return;
          edit((f) => zoneAppend(f, zoneKey, text));
          noteCustomToken(text);
        },
      });
    },
    [edit, onStyleSelect, noteCustomToken, pageVariant, select]
  );

  const wiring: DesignWiring = {
    pageVariant,
    styleSel,
    onStyleSelect,
    dragActive,
    onZoneDrop,
    levelFocus,
    edit,
    onRequestTextEdit: setTextDialog,
    onZoneQuickAdd,
    onZoneInsertAt,
    onZoneReplaceAt,
    onNotice: toast,
    onOpenPanel,
    onEndOfSection: (mods) => {
      sel.selectEos(mods);
      onOpenPanel?.('eos');
    },
    eosSelected: sel.eos,
    clearSelection: sel.clear,
  };

  const issuanceNames = project?.issuanceNames;
  const renderPanelBody = React.useCallback(
    (id: string): React.ReactNode => {
      if (!isFormaPanel(id) || !forma) return null;
      switch (id) {
        case 'typography':
          return (
            <div className="sfs-docsettings-host">
              <DocSettings
                doc={forma.docSettings}
                onUpdate={(d) => edit((f) => ({ ...f, docSettings: d }))}
                sections={['Typography']}
              />
            </div>
          );
        case 'margins':
          return (
            <div className="sfs-docsettings-host">
              <DocSettings
                doc={forma.docSettings}
                onUpdate={(d) => edit((f) => ({ ...f, docSettings: d }))}
                sections={['Page Margins (inches)']}
              />
            </div>
          );
        case 'formats':
          return (
            <div className="sfs-docsettings-host">
              <DocSettings
                doc={forma.docSettings}
                onUpdate={(d) => edit((f) => ({ ...f, docSettings: d }))}
                sections={['Formats']}
              />
            </div>
          );
        case 'layout':
          return (
            <LayoutPalette
              forma={forma}
              onEdit={edit}
              pageVariant={pageVariant}
              onPageVariant={onPageVariant}
            />
          );
        case 'watermark':
          return <WatermarkPanel forma={forma} onEdit={edit} />;
        case 'eos':
          return <EosPanel forma={forma} onEdit={edit} />;
        case 'levels':
          return (
            <LevelPalette
              forma={forma}
              editor={editor ?? null}
              levelFocus={levelFocus}
              onLevel={onLevelFocus}
              onEdit={edit}
              disabled={!canEdit}
              formatBar
            />
          );
        case 'tokens':
          return (
            <TokensPalette
              savedTokens={savedCustomTokens}
              recentTokens={recentCustomTokens}
              onSaveToken={(t) => persistSaved([...savedCustomTokens.filter((x) => x !== t), t])}
              onRemoveSavedToken={(t) => persistSaved(savedCustomTokens.filter((x) => x !== t))}
              onDragState={onDragState}
              onInsert={insertFromPalette}
              forma={forma}
              ctx={placeholderCtx ?? {}}
              projectTokens={projectTokens ?? []}
              issuanceNames={issuanceNames ?? []}
              onEdit={edit}
              masterAuthoring={!project}
            />
          );
        default:
          return null;
      }
    },
    // persistSaved is a plain closure over savedCustomTokens, which is
    // already a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      forma,
      edit,
      editor,
      pageVariant,
      onPageVariant,
      levelFocus,
      onLevelFocus,
      canEdit,
      savedCustomTokens,
      recentCustomTokens,
      onDragState,
      insertFromPalette,
      placeholderCtx,
      projectTokens,
      issuanceNames,
      project,
    ]
  );

  // Read-only: every panel but Levels (which disables its own controls) sits
  // in a disabled fieldset so the state is visible, not silent.
  const renderPanel = React.useCallback(
    (id: string): React.ReactNode => {
      const body = renderPanelBody(id);
      if (body == null || canEdit || id === 'levels') return body;
      return (
        <fieldset className="dsg-palette-fieldset" disabled>
          {body}
        </fieldset>
      );
    },
    [renderPanelBody, canEdit]
  );

  const wrap = (children: React.ReactNode) => (
    <CanvasSelectionContext.Provider value={canvasSelValue}>
      <TokenClipboardContext.Provider value={clipOps}>
        {children}
        {textDialog && <TextTokenDialog request={textDialog} onClose={() => setTextDialog(null)} />}
      </TokenClipboardContext.Provider>
    </CanvasSelectionContext.Provider>
  );

  return { wiring, renderPanel, wrap };
}
